/**
 * fish-pickup.js — pickup-slot selection + the slot checkout rules.
 * Loaded sitewide from layout/theme.liquid AFTER fish-cart-gate.js (it
 * registers with PondKing.Gate); only does anything on pages that render
 * #PickupSlotPicker (the cart page — snippets/pickup-slot-picker.liquid).
 *
 * - Picking a radio saves BOTH cart attributes via /cart/update.js:
 *     pickup_slot_handle   machine handle, consumed by the Flow workflows
 *     Pickup Slot          human label, visible on the order and emails
 * - Gate blocker 'no-pickup-slot': fish cart with no selected slot → block.
 * - Gate validator 'pickup-slot-fresh' (runs on real checkout submits):
 *   waits out any in-flight save, re-fetches the picker via the Section
 *   Rendering API (fresh capacity — slots can fill while the customer sits
 *   on the cart), and re-asserts the attributes if another tab cleared them.
 */
(function () {
  'use strict';

  window.PondKing = window.PondKing || {};
  if (window.PondKing.FishPickup) return;

  var strings = (window.PondKingFish && window.PondKingFish.strings) || {};
  var saving = null; // Promise<boolean> of the in-flight attribute save
  var lastChosenHandle = null; // this page-view's latest pick (optimistic UI)
  var activeDay = null; // day chip the customer is viewing (survives DOM swaps)

  function picker() {
    return document.getElementById('PickupSlotPicker');
  }

  function isFishCart(el) {
    return !!el && el.getAttribute('data-cart-has-fish') === 'true';
  }

  function checkedRadio(el) {
    return el ? el.querySelector('input[data-slot-handle]:checked') : null;
  }

  function slotRadios(el) {
    return el ? el.querySelectorAll('input[data-slot-handle]') : [];
  }

  function errorNode() {
    var el = picker();
    return el ? el.querySelector('[data-picker-error]') : null;
  }

  function showError(message) {
    var node = errorNode();
    var el = picker();
    if (node) {
      node.textContent = message;
      node.classList.remove('hide');
    }
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (node && node.focus) node.focus();
  }

  function clearError() {
    var node = errorNode();
    if (node) {
      node.textContent = '';
      node.classList.add('hide');
    }
  }

  /* ---------------- day-first picker ---------------- */

  // The picker renders day chips + one group of radios per day
  // (snippets/pickup-slot-picker.liquid). Switching days only hides the other
  // groups — every radio stays in the DOM for the gate, the validator and the
  // native form post. The poller and refreshPicker both replace the picker's
  // innerHTML, so the viewed day lives here and is re-applied after each swap.

  function dayOf(radio) {
    var panel = radio && radio.closest('[data-pickup-day-panel]');
    return panel ? panel.getAttribute('data-pickup-day-panel') : null;
  }

  function markSelectedDay(el) {
    if (!el) return;
    var checked = checkedRadio(el);
    var day = dayOf(checked);
    var buttons = el.querySelectorAll('[data-pickup-day]');
    for (var i = 0; i < buttons.length; i++) {
      if (day && buttons[i].getAttribute('data-pickup-day') === day) {
        buttons[i].setAttribute('data-has-selection', '');
      } else {
        buttons[i].removeAttribute('data-has-selection');
      }
    }
  }

  function showDay(day) {
    var el = picker();
    if (!el) return;
    var panels = el.querySelectorAll('[data-pickup-day-panel]');
    if (!panels.length) return;
    el.classList.add('pickup-slot-picker--js');
    // Fall back when the day vanished (every window filled) or none is set:
    // the day holding the checked slot, else the first open day.
    var exists = false;
    for (var i = 0; i < panels.length; i++) {
      if (panels[i].getAttribute('data-pickup-day-panel') === day) { exists = true; break; }
    }
    if (!exists) day = dayOf(checkedRadio(el)) || panels[0].getAttribute('data-pickup-day-panel');
    activeDay = day;
    for (var j = 0; j < panels.length; j++) {
      panels[j].hidden = panels[j].getAttribute('data-pickup-day-panel') !== day;
    }
    var buttons = el.querySelectorAll('[data-pickup-day]');
    for (var k = 0; k < buttons.length; k++) {
      buttons[k].setAttribute('aria-pressed', buttons[k].getAttribute('data-pickup-day') === day ? 'true' : 'false');
    }
    markSelectedDay(el);
  }

  function initDays() {
    var el = picker();
    if (!el) return;
    // Opening day: this page-view's pick, else the one saved on the cart.
    var handle = lastChosenHandle || el.getAttribute('data-selected-handle');
    var radio = handle ? el.querySelector('input[data-slot-handle="' + handle + '"]') : null;
    showDay(activeDay || dayOf(radio));
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target && evt.target.closest ? evt.target.closest('[data-pickup-day]') : null;
    if (!btn || !btn.closest('#PickupSlotPicker')) return;
    evt.preventDefault(); // type="button" already; belt and braces inside #CartPageForm
    showDay(btn.getAttribute('data-pickup-day'));
  });

  function saveSlot(handle, label) {
    var attributes = { pickup_slot_handle: handle };
    attributes['Pickup Slot'] = label;
    var request = fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart update ' + r.status);
        return r.json();
      })
      .then(function () {
        var el = picker();
        if (el) el.setAttribute('data-selected-handle', handle);
        clearError();
        if (window.PondKing.Gate) window.PondKing.Gate.evaluate();
        return true;
      })
      .catch(function () {
        // Don't keep re-asserting a pick that never saved — the next poller
        // swap reverting to server truth is honest; the error explains.
        lastChosenHandle = null;
        showError(strings.slotSaveFailed || 'Could not save your pickup time. Please try again.');
        return false;
      })
      .then(function (ok) {
        // Only release the latch if a newer save hasn't replaced it — the
        // checkout validator must always await the LATEST selection.
        if (saving === request) saving = null;
        return ok;
      });
    saving = request;
    return request;
  }

  document.addEventListener('change', function (evt) {
    var input = evt.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-slot-handle')) return;
    if (!input.closest('#PickupSlotPicker')) return;
    lastChosenHandle = input.getAttribute('data-slot-handle');
    markSelectedDay(picker());
    saveSlot(lastChosenHandle, input.value);
  });

  // The picker opts into the fish-inventory.js poller (live spot counts).
  // A swap that raced an in-flight save re-renders from the PRE-save cart
  // attributes — re-assert the customer's latest pick so it never visually
  // reverts. (If the slot just filled up, its radio is gone: the server
  // markup's stale-selection notice explains.)
  document.addEventListener('fish:refreshed', function (evt) {
    if (!evt.detail || evt.detail.nodeId !== 'PickupSlotPicker') return;
    var el = picker();
    var handle = lastChosenHandle || (el && el.getAttribute('data-selected-handle'));
    if (el && handle) restoreChecked(el, handle);
    initDays();
  });

  function restoreChecked(el, handle) {
    var radio = el.querySelector('input[data-slot-handle="' + handle + '"]');
    if (radio && !radio.checked) {
      radio.checked = true;
      // The gate's own fish:refreshed listener ran first, against the
      // swapped (unchecked) DOM — re-evaluate now that the pick is back.
      if (window.PondKing.Gate) window.PondKing.Gate.evaluate();
    }
  }

  /**
   * Re-fetch the picker's owning section (main-cart) and swap #PickupSlotPicker
   * so slot capacity/checked state reflect the live store.
   */
  function refreshPicker() {
    var el = picker();
    if (!el) return Promise.resolve();
    var sectionId = el.getAttribute('data-section-id');
    if (!sectionId) return Promise.resolve();
    var params = new URLSearchParams(window.location.search);
    params.set('section_id', sectionId);
    params.set('_fts', String(Date.now()));
    return fetch(window.location.pathname + '?' + params.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('section fetch ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('PickupSlotPicker');
        var live = picker();
        if (!fresh || !live) return;
        live.innerHTML = fresh.innerHTML;
        for (var i = 0; i < fresh.attributes.length; i++) {
          var attr = fresh.attributes[i];
          if (attr.name.indexOf('data-') === 0) live.setAttribute(attr.name, attr.value);
        }
        initDays();
      });
  }

  // Quantity changes re-render cart lines without a reload; keep remaining-spot
  // counts and the fish/non-fish state of the picker honest.
  var refreshTimer = null;
  document.addEventListener('cart:updated', function () {
    var el = picker();
    // Non-fish carts render an empty wrapper — no reason to refetch the section.
    if (!el || el.getAttribute('data-cart-has-fish') !== 'true') return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      Promise.resolve(saving).then(refreshPicker).catch(function () { /* next cycle */ });
    }, 800);
  });

  /* ---------------- gate wiring ---------------- */

  function registerWithGate() {
    if (!window.PondKing.Gate) return; // load-order guard; gate loads first

    window.PondKing.Gate.addBlocker('no-pickup-slot', function () {
      var el = picker();
      if (!el || !isFishCart(el)) return null;
      if (checkedRadio(el)) return null;
      var hasOptions = slotRadios(el).length > 0;
      return {
        message: hasOptions
          ? (strings.noSlotSelected || 'Please choose a pickup time before checking out.')
          : (strings.noSlotsThisWeek || 'No pickup times are available this week.')
      };
    });

    window.PondKing.Gate.addValidator('pickup-slot-fresh', function () {
      var el = picker();
      if (!el || !isFishCart(el)) return Promise.resolve(null);
      return validateSlot();
    });
  }

  /**
   * Full pre-checkout slot validation: wait out any in-flight save, re-fetch
   * the picker (fresh capacity — slots fill while the customer shops), then
   * confirm a still-open slot is selected and its attributes are on the cart.
   * Resolves null when good to go, or {message} (also shown at the picker).
   * Used by the cart-page checkout gate AND the fish page's straight-to-
   * checkout flow (fish-table.js).
   */
  function validateSlot() {
    var el = picker();
    if (!el) return Promise.resolve(null); // no picker on this page
    return Promise.resolve(saving)
      .then(refreshPicker)
      .then(function () {
        var freshEl = picker();
        var checked = checkedRadio(freshEl);
        if (!checked) {
          var hasOptions = slotRadios(freshEl).length > 0;
          var hadPick = lastChosenHandle || (freshEl && freshEl.getAttribute('data-selected-handle'));
          var message = !hasOptions
            ? (strings.noSlotsThisWeek || 'No pickup times are available this week.')
            : hadPick
              ? (strings.slotGone || 'That pickup time just filled up — please choose another.')
              : (strings.noSlotSelected || 'Please choose a pickup time before checking out.');
          showError(message);
          return { message: message };
        }
        // Re-assert the attributes in case another tab cleared/changed them.
        return fetch('/cart.js?_fts=' + Date.now(), { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var attrs = cart.attributes || {};
            var handle = checked.getAttribute('data-slot-handle');
            if (attrs.pickup_slot_handle === handle) return null;
            return saveSlot(handle, checked.value).then(function (ok) {
              if (ok) return null;
              return { message: strings.slotSaveFailed || 'Could not save your pickup time. Please try again.' };
            });
          });
      });
  }

  registerWithGate();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDays);
  } else {
    initDays();
  }

  window.PondKing.FishPickup = {
    refreshPicker: refreshPicker,
    validate: validateSlot,
    present: function () { return !!picker(); },
    hasSelection: function () { return !!checkedRadio(picker()); },
    // Re-save the currently checked slot's attributes (used by the gate after
    // "Clear cart & add" wipes attributes — a slot picked moments earlier on
    // the fish page must survive the clear). Resolves {needed, ok}: needed
    // distinguishes "nothing to re-save" (fine) from "re-save FAILED" (the
    // caller must not proceed claiming the pickup time is confirmed).
    reassert: function () {
      var checked = checkedRadio(picker());
      if (!checked) return Promise.resolve({ needed: false, ok: true });
      return saveSlot(checked.getAttribute('data-slot-handle'), checked.value)
        .then(function (ok) { return { needed: true, ok: ok }; });
    }
  };
})();
